/**
 * The uncaptured gap as shown: the Dashboard's "uncaptured" and package "gap", the page's gap, the
 * end of the `/review` belt and the brief's prose (owner D4, 2026-11-03; TRAFFIC_ESTIMATE_CUTOVER.md
 * §3 stage 4, TECHNICAL_DEBT §0cu).
 *
 * **What the number is.** max(0, ceiling − realized) per page, both sides on our own click curve
 * (`ctrForPosition` over the page's ranked keywords): the ceiling at the target position, realized
 * at the positions held. DataForSEO's estimate is not read, so its next change cannot move scoring.
 * Ranking and package selection use it as it is.
 *
 * **What is shown.**
 * - **Search Console on:** our curve reads about 5× real clicks on ManyRequests (measured
 *   2026-11-03), so the shown number is converted with the site's own ratio: Search Console clicks ÷
 *   our curve, over the pages with a scored gap, one factor for the whole site. The order does not
 *   change. It reads "modelled clicks a month" with "≈": a conversion, not a count.
 * - **Without it:** nothing to convert against. The number is shown as it is and labelled as demand
 *   on our model, never "visits".
 *
 * Neither form may read like a click line ("Search Console clicks, 14 days before crawl", "clicks
 * lost", "real clicks in the 28 days to …"; memory: confusable states need different copy).
 */

/** The site's factor from our curve to Search Console clicks, or absent where it cannot be set. */
export interface UncapturedGapScale {
  /** Search Console clicks in the window on the pages with a scored gap. */
  clicks: number;
  /** Our curve's figure for the same pages (their realized side). */
  curve: number;
  /** clicks ÷ curve. */
  ratio: number;
  /** Pages with a scored gap the two sums run over. */
  pages: number;
  windowDays: number;
  /** Last day of the window, `YYYY-MM-DD`. */
  windowEnd: string;
}

/** The factor, or null when our curve gives these pages nothing to divide by. */
export function uncapturedGapScale(input: {
  clicks: number;
  curve: number;
  pages: number;
  windowDays: number;
  windowEnd: string;
}): UncapturedGapScale | null {
  if (!(input.curve > 0) || !(input.pages > 0) || !(input.clicks >= 0)) return null;
  return { ...input, ratio: input.clicks / input.curve };
}

/** The number to show for a gap on our curve. */
export function uncapturedGapShown(gap: number, scale: UncapturedGapScale | null | undefined): number {
  return scale ? gap * scale.ratio : gap;
}

const thousands = (n: number): string => Math.round(n).toLocaleString('en-US');

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function dayMonth(isoDay: string): string {
  const [, m, d] = isoDay.split('-').map(Number);
  return m >= 1 && m <= 12 && d ? `${d} ${MONTHS[m - 1]}` : isoDay;
}

export interface UncapturedGapStat {
  value: string;
  word: string;
  wordFirst: boolean;
}

/** The strip's total: the unit is said here once; package and page stats say "gap" beside it. */
export function uncapturedGapHeadlineStat(gapSum: number, scale: UncapturedGapScale | null | undefined): UncapturedGapStat {
  return scale
    ? { value: `≈${thousands(uncapturedGapShown(gapSum, scale))}`, word: 'modelled clicks a month uncaptured', wordFirst: false }
    : { value: thousands(gapSum), word: 'uncaptured on our demand model', wordFirst: false };
}

/** A package's or a page's gap, beside the strip's total. */
export function uncapturedGapStat(gap: number, scale: UncapturedGapScale | null | undefined): UncapturedGapStat {
  return scale
    ? { value: `≈${thousands(uncapturedGapShown(gap, scale))}`, word: 'gap', wordFirst: true }
    : { value: thousands(gap), word: 'gap', wordFirst: true };
}

/** Said once under the strip's numbers: what "uncaptured" is, and where Search Console is on, how it was converted. */
export function uncapturedGapNote(scale: UncapturedGapScale | null | undefined): string {
  if (scale) {
    return (
      'Uncaptured is what these pages could add by ranking higher, on our own model of searches and positions, ' +
      `converted to clicks with this site's ratio: ${thousands(scale.clicks)} real clicks in Search Console against ` +
      `${thousands(scale.curve)} on our model for the same pages, in the ${scale.windowDays} days to ${dayMonth(scale.windowEnd)}.`
    );
  }
  return (
    'Uncaptured is what these pages could add by ranking higher, on our own model of searches and positions. ' +
    'It is not a count of visits.'
  );
}

/**
 * The gap in a sentence of the brief's prose: "about 418 modelled clicks a month of demand" or
 * "2,266 of demand on our model".
 */
export function uncapturedGapPhrase(gap: number, scale: UncapturedGapScale | null | undefined): string {
  return scale
    ? `about ${thousands(uncapturedGapShown(gap, scale))} modelled clicks a month of demand`
    : `${thousands(gap)} of demand on our model`;
}

/** The end of the `/review` belt: what this session's sent pages hold of the gap. */
export function claimedGapLine(claimed: number, scale: UncapturedGapScale | null | undefined): { value: string; rest: string } {
  return scale
    ? { value: `≈${thousands(uncapturedGapShown(claimed, scale))}`, rest: 'modelled clicks a month of uncaptured demand now have work against them.' }
    : { value: thousands(claimed), rest: 'of uncaptured demand, on our model, now has work against it.' };
}
